/**
 * Branded types for email addresses with format validation.
 * @example
 * ```typescript
 * // Creating email addresses with validation
 * const email = EmailAddress.create('user@example.com'); // Valid
 * // This will throw at runtime:
 * EmailAddress.create('invalid');  // Error: missing @
 * EmailAddress.create('user@');    // Error: missing domain
 * // Type-safe usage - compiler knows the constraint
 * function sendEmail(to: EmailAddress, subject: string): void {
 *   // to is guaranteed to be valid email format
 * }
 * ```
 */

import { z } from "zod";
import { InvalidEmailError } from "../errors/invalid-email.error.js";

export const EmailAddressSchema = z
  .string()
  .refine((val) => isValidEmail(val), {
    message: "Invalid email address format",
  })
  .brand("EmailAddress");

/**
 * Branded type for validated email addresses.
 *
 * Basic format validation: local@domain.tld
 * - Contains exactly one @
 * - Has non-empty local part
 * - Has non-empty domain with at least one dot
 *
 * Note: Full RFC 5322 validation is intentionally not implemented as it's
 * overly complex. This provides pragmatic validation for common email formats.
 *
 * Used for: Email sender/recipient addresses
 */

export type EmailAddress = z.infer<typeof EmailAddressSchema>;

/**
 * Basic email format validation regex.
 *
 * Matches: local@domain.tld
 * - Local part: one or more non-whitespace, non-@ characters
 * - @ symbol
 * - Domain: one or more non-whitespace, non-@ characters with at least one dot
 *
 * Note: This is intentionally simple. Full RFC 5322 compliance is overkill
 * for most use cases and can reject valid addresses that work in practice.
 */
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function normalizeEmail(value: string): string {
  return value.trim().toLowerCase();
}

function isValidEmail(value: string): boolean {
  return typeof value === "string" && EMAIL_REGEX.test(value);
}

export const EmailAddress = {
  /**
   * Create an EmailAddress from a string, with validation and normalization.
   * Normalization: - Trims whitespace - Converts to lowercase
   * @param value - String to convert (must be valid email format)
   * @returns EmailAddress branded value (normalized)
   * @throws InvalidEmailError if value is not valid email format
   */
  create(value: string): EmailAddress {
    const normalized = normalizeEmail(value);
    if (!isValidEmail(normalized)) {
      throw new InvalidEmailError(value);
    }
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Required for branded type construction
    return normalized as EmailAddress;
  },

  unsafeCreate(value: string): EmailAddress {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Required for branded type construction
    return normalizeEmail(value) as EmailAddress;
  },

  /**
   * Check if a string is a valid email address. Note: Does NOT normalize - checks the value as-is.
   * @param value - String to validate
   * @returns true if valid email format
   */
  isValid(value: string): boolean {
    return isValidEmail(value);
  },

  /**
   * Parse a value as EmailAddress or return undefined if invalid.
   * Normalization: - Trims whitespace - Converts to lowercase
   * @param value - String to parse
   * @returns EmailAddress if valid (normalized), undefined otherwise
   */
  tryCreate(value: string): EmailAddress | undefined {
    const normalized = normalizeEmail(value);
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Required for branded type construction
    return isValidEmail(normalized) ? (normalized as EmailAddress) : undefined;
  },
} as const;

// Canonical placeholder address used when scrubbing PII from an anonymized
// participant.
export const ANONYMIZED_EMAIL: EmailAddress =
  EmailAddress.create("anonymous@anon.com");
